import { observer } from 'mobx-react-lite'

import { Form, FormField } from '@/Core/UI/Form'
import { EntityBody, EntityLayout, FieldCell, FieldGrid, FieldSection, FileFieldCell } from '@/Ship/Components/EntityCard'

import { LetterTemplateVariables } from '../Components/LetterTemplateVariables'
import { LETTER_TEMPLATE_LABELS as L, type LetterTemplateFormValues } from '../Contracts/LetterTemplate'

interface LetterTemplateFormContext {
    isEditable?: boolean
    values?: LetterTemplateFormValues
}

/**
 * Селект прив'язок (і перемикач «Офіційний лист заявки» провайдера «Заявки»)
 * — лише для свого провайдера, і в перегляді теж: там ядро
 * `depFn` поля не викликає, і картка шаблону контакту показувала б порожні
 * «Клієнт» і «Перевізник». Окремий observer — читає провайдер замість шаблону.
 */
const BindingCell = observer(
    ({
        formContext,
        provider,
        name,
    }: {
        formContext: LetterTemplateFormContext
        provider: 'client' | 'carrier' | 'application'
        name: string
    }) =>
        formContext.values?.provider === provider ? (
            <FieldCell wide>
                <FormField name={name} />
            </FieldCell>
        ) : null
)

/**
 * Форма шаблону листа (`SPEC-013` §9.1; легасі `ListLetterTemplateFormTemplate.jsx`):
 * порядок полів легасі — провайдер, клієнти чи перевізники, «За
 * замовчуванням», назва, тема, три рядки адрес, шаблон, змінні, вкладення,
 * сортування; далі службова інформація.
 *
 * «Клієнт» і «Перевізник» видно лише для свого провайдера (`depFn` ресурсу).
 * Вкладення — двокроково, тож їх можна додати вже в новий шаблон.
 *
 * ⚠️ Значень, які редагують, шаблон не читає — лише через окремі
 * observer-компоненти (`LetterTemplateVariables`), інакше ядровий `FormField`
 * перемонтовував би всі поля на кожну літеру (`SPEC-002` §4.2).
 */
export const ListLetterTemplateFormTemplate = ({ formContext }: { formContext: LetterTemplateFormContext }) => {
    const uuid = formContext.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''

    return (
        <EntityLayout>
            <EntityBody
                main={
                    <>
                        <FieldSection title="Шаблон листа">
                            <Form formContext={formContext}>
                                <FieldGrid>
                                    <FieldCell>
                                        <FormField name="provider" />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="isDefault" />
                                    </FieldCell>
                                    <BindingCell formContext={formContext} provider="client" name="clients" />
                                    <BindingCell formContext={formContext} provider="carrier" name="carriers" />
                                    <BindingCell
                                        formContext={formContext}
                                        provider="application"
                                        name="isOfficialApplication"
                                    />
                                    <FieldCell wide>
                                        <FormField name="title" />
                                    </FieldCell>
                                    <FieldCell wide>
                                        <FormField name="subject" />
                                    </FieldCell>
                                    <FieldCell wide>
                                        <FormField name="toWhom" />
                                    </FieldCell>
                                    <FieldCell wide>
                                        <FormField name="cc" />
                                    </FieldCell>
                                    <FieldCell wide>
                                        <FormField name="bcc" />
                                    </FieldCell>
                                    <FieldCell wide>
                                        <FormField name="body" />
                                    </FieldCell>
                                    <FieldCell wide>
                                        <LetterTemplateVariables formContext={formContext} />
                                    </FieldCell>
                                    <FieldCell wide>
                                        <FileFieldCell formContext={formContext} name="attachments" label={L.attachments} />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="sorting" />
                                    </FieldCell>
                                </FieldGrid>
                            </Form>
                        </FieldSection>
                        <FieldSection title="Службова інформація" hidden={isNew}>
                            <Form formContext={formContext}>
                                <FieldGrid>
                                    <FieldCell>
                                        <FormField name="id" />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="createdAt" />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="createdBy" />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="updatedAt" />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="updatedBy" />
                                    </FieldCell>
                                </FieldGrid>
                            </Form>
                        </FieldSection>
                    </>
                }
            />
        </EntityLayout>
    )
}
